import { apiRequest } from './api';
import { AuthData, LoginCredentials, User } from '../types';

// Função para converter dados do backend (PT-BR) para frontend (EN)
const convertUserFromBackend = (backendUser: any): User => {
  return {
    id: backendUser.id,
    nome: backendUser.nome,
    usuario: backendUser.usuario,
    email: backendUser.email,
    perfil: backendUser.perfil,
    status: backendUser.status,
    telefone: backendUser.telefone,
    avatar: backendUser.avatar,
    funcionario_id: backendUser.funcionario_id ?? undefined,
    escritorio_ids: backendUser.escritorio_ids,
    grupo_id: backendUser.grupo_id ?? null,
    permissoes_custom: backendUser.permissoes_custom ?? null,
    permissions: backendUser.permissions ?? [],
    ultimo_login_em: backendUser.ultimo_login_em,
    sigame: backendUser.sigame ?? false,
    ticket_novo: backendUser.ticket_novo ?? false,
    telefones_sigame: backendUser.telefones_sigame ?? null,
    sigame_hora_inicio: backendUser.sigame_hora_inicio ?? null,
    sigame_hora_fim: backendUser.sigame_hora_fim ?? null,
    createdAt: backendUser.createdAt,
    updatedAt: backendUser.updatedAt,
  };
};

const convertProfileDataToBackend = (frontendData: Partial<User>): any => {
  return {
    name: frontendData.nome,
    phone: frontendData.telefone,
    avatar: frontendData.avatar,
    sigame: frontendData.sigame,
    ticket_novo: frontendData.ticket_novo,
    telefones_sigame: frontendData.telefones_sigame,
    sigame_hora_inicio: frontendData.sigame_hora_inicio,
    sigame_hora_fim: frontendData.sigame_hora_fim,
  };
};

export const authService = {
  // Login do usuário
  login: async (credentials: LoginCredentials): Promise<AuthData> => {
    const response = await apiRequest.post<AuthData>('/auth/login', credentials);
    
    // Converter usuário do backend para formato do frontend
    const convertedUser = convertUserFromBackend(response.data!.user);
    
    return {
      user: convertedUser,
      token: response.data!.token,
      expiresIn: response.data!.expiresIn
    };
  },

  unlockWithPassword: async (credentials: LoginCredentials): Promise<AuthData> => {
    const response = await apiRequest.post<AuthData>(
      '/auth/login',
      credentials,
      { skipAuthRedirect: true } as any
    );

    const convertedUser = convertUserFromBackend(response.data!.user);
    localStorage.setItem('sysup_token', response.data!.token);

    return {
      user: convertedUser,
      token: response.data!.token,
      expiresIn: response.data!.expiresIn
    };
  },



  // Logout do usuário
  logout: async (): Promise<void> => {
    await apiRequest.post('/auth/logout');
  },

  // Obter dados do usuário logado
  getMe: async (): Promise<User> => {
    const response = await apiRequest.get<any>('/auth/me');
    return convertUserFromBackend(response.data!);
  },

  // Atualizar perfil do usuário
  updateProfile: async (data: Partial<User>): Promise<User> => {
    // Converter dados para formato do backend
    const backendData = convertProfileDataToBackend(data);
    const response = await apiRequest.put<any>('/auth/me', backendData);
    return convertUserFromBackend(response.data!);
  },

  // Alterar senha do usuário
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiRequest.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data!;
  },

  // Renovar token
  refreshToken: async (token: string): Promise<{ token: string; expiresIn: string }> => {
    const response = await apiRequest.post<{ token: string; expiresIn: string }>('/auth/refresh', { token });
    return response.data!;
  },

  // Solicitar reset de senha via OTP WhatsApp
  enviarOtpEsqueciSenha: async (usuario: string, email: string): Promise<{ success: boolean; message: string; data?: { telefone_mascarado: string } }> => {
    const response = await apiRequest.post<{ success: boolean; message: string; data?: { telefone_mascarado: string } }>(
      '/auth/enviar-otp-esqueci-senha',
      { usuario, email }
    );
    return response as any;
  },

  // Validar código OTP de esqueci senha
  validarOtpEsqueciSenha: async (usuario: string, email: string, codigo: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest.post<{ success: boolean; message: string }>(
      '/auth/validar-otp-esqueci-senha',
      { usuario, email, codigo }
    );
    return response as any;
  },

  // Enviar código OTP para confirmação da nova senha
  enviarOtpNovaSenha: async (usuario: string, email: string, password: string): Promise<{ success: boolean; message: string; data?: { telefone_mascarado: string; is_admin_destinatario?: boolean } }> => {
    const response = await apiRequest.post<{ success: boolean; message: string; data?: { telefone_mascarado: string; is_admin_destinatario?: boolean } }>(
      '/auth/enviar-otp-nova-senha',
      { usuario, email, password }
    );
    return response as any;
  },

  // Redefinir senha após confirmação do segundo OTP
  resetPasswordWithOtp: async (usuario: string, email: string, password: string, codigo: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest.post<{ success: boolean; message: string }>(
      '/auth/reset-password-otp',
      { usuario, email, password, codigo }
    );
    return response as any;
  },

  // Solicitar reset de senha
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiRequest.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data!;
  },

  // Redefinir senha
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const response = await apiRequest.post<{ message: string }>('/auth/reset-password', { token, password });
    return response.data!;
  },

  // Verificar se o usuário está autenticado
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('sysup_token');
    return !!token;
  },

  // Obter token do localStorage
  getToken: (): string | null => {
    return localStorage.getItem('sysup_token');
  },

  // Salvar token no localStorage
  setToken: (token: string): void => {
    localStorage.setItem('sysup_token', token);
  },

  // Remover token do localStorage
  removeToken: (): void => {
    localStorage.removeItem('sysup_token');
  },
}; 
